import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, X, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Switch } from './ui/switch';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Delay showing banner slightly for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleManagePreferences = () => {
    setShowPreferences(true);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Icon and Content */}
              <div className="flex-1 flex items-start gap-4">
                <div className="bg-[#0d6571] p-3 rounded-full flex-shrink-0">
                  <Cookie className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    This website uses cookies
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    This website uses cookies to personalize content, provide social media features, 
                    and analyze our traffic. You can choose to accept all cookies or manage your preferences. 
                    By clicking "Accept all," you consent to the use of all cookies.
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-6 py-3 font-semibold whitespace-nowrap"
                >
                  Accept all
                </Button>
                <Button
                  onClick={handleRejectNonEssential}
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 rounded-full px-6 py-3 font-semibold hover:bg-gray-50 whitespace-nowrap"
                >
                  Reject non-essential
                </Button>
                <Button
                  onClick={handleManagePreferences}
                  variant="outline"
                  className="border-2 border-[#0d6571] text-[#0d6571] rounded-full px-6 py-3 font-semibold hover:bg-[#0d6571]/5 whitespace-nowrap"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage preferences
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Settings className="w-6 h-6 text-[#0d6571]" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Manage your cookie preferences. You can enable or disable different types of cookies below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Necessary Cookies */}
            <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Necessary Cookies
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    These cookies are essential for the website to function properly. They enable 
                    basic functions like page navigation and access to secure areas. The website 
                    cannot function properly without these cookies.
                  </p>
                </div>
                <div className="ml-4">
                  <Switch
                    checked={preferences.necessary}
                    disabled={true}
                    className="opacity-50"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 italic">Always Active</p>
            </div>

            {/* Analytics Cookies */}
            <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-[#0d6571]/30 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Analytics Cookies
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    These cookies help us understand how visitors interact with our website by 
                    collecting and reporting information anonymously. This helps us improve our 
                    website's performance and user experience.
                  </p>
                </div>
                <div className="ml-4">
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) =>
                      setPreferences({ ...preferences, analytics: checked })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-[#0d6571]/30 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Marketing Cookies
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    These cookies are used to track visitors across websites. They are used to 
                    display ads that are relevant and engaging for individual users, thereby 
                    creating value for publishers and third-party advertisers.
                  </p>
                </div>
                <div className="ml-4">
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(checked) =>
                      setPreferences({ ...preferences, marketing: checked })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Personalization Cookies */}
            <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-[#0d6571]/30 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Personalization Cookies
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    These cookies enable the website to remember choices you make (such as your 
                    user name, language, or region) and provide enhanced, more personal features.
                  </p>
                </div>
                <div className="ml-4">
                  <Switch
                    checked={preferences.personalization}
                    onCheckedChange={(checked) =>
                      setPreferences({ ...preferences, personalization: checked })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button
              onClick={handleSavePreferences}
              className="flex-1 bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full py-3 font-semibold"
            >
              Save Preferences
            </Button>
            <Button
              onClick={handleAcceptAll}
              className="flex-1 bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full py-3 font-semibold"
            >
              Accept All
            </Button>
            <Button
              onClick={handleRejectNonEssential}
              variant="outline"
              className="flex-1 border-2 border-gray-300 text-gray-700 rounded-full py-3 font-semibold hover:bg-gray-50"
            >
              Reject Non-Essential
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
